import { required } from '@reliance/forms-js/lib/validation';
import { CommunicationTreatment } from '@reliance/types/enums/CommunicationTreatment';
import { EnabledDisabled } from '@reliance/types/enums/EnabledDisabled';
import { Inmate } from '@reliance/types/models/Inmate';
import moment from 'moment';
import React, { useMemo, useState } from 'react';
import { Button, Card, Col, Form, InputGroup, Row } from 'react-bootstrap';
import { Form as FinalForm } from 'react-final-form';
import api from '../../common/api';
import { Select } from '../../form/Select';
import { InputType, TextInput } from '../../form/TextInput';
import { formatPhoneNumber } from '../../format/PhoneNumber';
import { useAlerts } from '../../alert/AlertContext';

type InmateEditProps = {
  inmate: Inmate;
  onSave: (savedInmate: Inmate) => void;
};

type InmateEditFormValues = {
  firstName: string;
  middleName: string;
  lastName: string;
  dateOfBirth: string;
  newAccount: string;
  pin: string;
  personId: string;
  bookingId: string;
  status: EnabledDisabled;
  phoneStatus: EnabledDisabled;
  textStatus: EnabledDisabled;
  audioStatus: EnabledDisabled;
  tvStreamingStatus: EnabledDisabled;
  voicemailStatus: EnabledDisabled;
  gamesEnabled: EnabledDisabled;
  referenceMaterialEnabled: EnabledDisabled;
  communicationTreatment: CommunicationTreatment;
  faceLoginStatus: EnabledDisabled;
  recordFaceLoginVideo: string;
};

const InmateEdit: React.FC<InmateEditProps> = (props) => {
  const { inmate, onSave } = props;
  const alerts = useAlerts();
  const [reassigningAni, setReassigningAni] = useState(false);

  const initialValues = useMemo<InmateEditFormValues>(() => {
    const dateOfBirth = inmate.dateOfBirth
      ? (typeof inmate.dateOfBirth === 'object' && inmate.dateOfBirth.date
          ? moment(inmate.dateOfBirth.date).format('YYYY-MM-DD')
          : moment(inmate.dateOfBirth as any).format('YYYY-MM-DD'))
      : '';

    return {
      firstName: inmate.firstName || '',
      middleName: inmate.middleName || '',
      lastName: inmate.lastName || '',
      dateOfBirth,
      newAccount: inmate.account || '',
      pin: inmate.pin || '',
      personId: inmate.personId || '',
      bookingId: inmate.bookingId || '',
      status: inmate.status || EnabledDisabled.ENABLED,
      phoneStatus: inmate.phoneStatus || EnabledDisabled.ENABLED,
      textStatus: inmate.textStatus || EnabledDisabled.ENABLED,
      audioStatus: inmate.audioStatus || EnabledDisabled.ENABLED,
      tvStreamingStatus: inmate.tvStreamingStatus || EnabledDisabled.ENABLED,
      voicemailStatus: inmate.voicemailStatus || EnabledDisabled.ENABLED,
      gamesEnabled: inmate.gamesEnabled || EnabledDisabled.ENABLED,
      referenceMaterialEnabled: inmate.referenceMaterialEnabled || EnabledDisabled.ENABLED,
      communicationTreatment: inmate.communicationTreatment || CommunicationTreatment.STANDARD,
      faceLoginStatus: inmate.faceLoginStatus || EnabledDisabled.ENABLED,
      recordFaceLoginVideo: inmate.recordFaceLoginVideo ? 'true' : 'false',
    };
  }, [inmate]);

  const handleSubmit = async (values: InmateEditFormValues) => {
    const recordFaceLoginVideo = values.recordFaceLoginVideo === 'true';

    const updatedInmate: Partial<Inmate> & { newAccount: string } = {
      ...inmate,
      firstName: values.firstName,
      middleName: values.middleName,
      lastName: values.lastName,
      dateOfBirth: values.dateOfBirth ? moment(values.dateOfBirth).toDate() as any : null,
      account: values.newAccount,
      newAccount: values.newAccount,
      pin: values.pin,
      personId: values.personId,
      bookingId: values.bookingId,
      status: values.status,
      phoneStatus: values.phoneStatus,
      textStatus: values.textStatus,
      audioStatus: values.audioStatus,
      voicemailStatus: values.voicemailStatus,
      gamesEnabled: values.gamesEnabled,
      referenceMaterialEnabled: values.referenceMaterialEnabled,
      communicationTreatment: values.communicationTreatment,
      recordFaceLoginVideo,
    };

    const payload = {
      ...updatedInmate,
      faceLoginStatus: values.faceLoginStatus,
      recordFaceLoginVideo,
      tvStreamingStatus: values.tvStreamingStatus,
    };

    try {
      await api.put(
        `/inmate/${inmate.account}?facilityId=${inmate.facility.id}`,
        payload
      );
      alerts.success('The inmate was successfully changed');
      onSave(updatedInmate as Inmate);
    } catch (error: any) {
      const response = error.response;
      if (response?.status === 503) {
        alerts.warning('The inmate was saved, but we were unable to refresh the site API cache');
        onSave(updatedInmate as Inmate);
      } else if (response?.status === 409) {
        alerts.error('An existing inmate at this facility has this account');
      } else {
        alerts.error('An error occurred while saving the changes');
      }
      throw error;
    }
  };

  const handleReassignAni = async () => {
    setReassigningAni(true);
    try {
      await api.post<{ success: boolean; number: string }>(
        `/inmate-device/${inmate.id}/ani`,
        { returnCurrent: true }
      );
      alerts.success('The inmate\'s ANI was successfully reassigned');
      onSave(inmate); // This will trigger a refresh of the inmate data
    } catch (error) {
      alerts.error('An error occurred while reassigning the inmate\'s ANI');
    } finally {
      setReassigningAni(false);
    }
  };

  return (
    <Card>
      <FinalForm
        initialValues={initialValues}
        onSubmit={handleSubmit}
        render={({ handleSubmit, submitting, hasValidationErrors }) => (
          <form onSubmit={handleSubmit}>
            <Card.Body>
              <Row>
                <Col md={4}>
                  <TextInput
                    name="firstName"
                    label="First Name:"
                    validationRules={[required]} />
                  <TextInput
                    name="middleName"
                    label="Middle Name:" />
                  <TextInput
                    name="lastName"
                    label="Last Name:"
                    validationRules={[required]} />
                  <TextInput
                    name="dateOfBirth"
                    label="Date of Birth:"
                    placeholder="Date of Birth"
                    type={InputType.date} />
                </Col>

                <Col md={4}>
                  <TextInput
                    name="newAccount"
                    label="Account:"
                    validationRules={[required]} />
                  <TextInput
                    name="pin"
                    label="PIN:"
                    validationRules={[required]} />
                  <TextInput
                    name="personId"
                    label="Person ID:" />
                  <TextInput
                    name="bookingId"
                    label="Booking ID:" />
                  <Form.Group>
                    <Form.Label>SMS Number:</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="text"
                        value={inmate.phoneNumber ? formatPhoneNumber(inmate.phoneNumber) : 'NO ANI ASSIGNED'}
                        disabled />
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={(e) => {
                          e.preventDefault();
                          handleReassignAni();
                        }}
                        disabled={reassigningAni}>
                        Reassign ANI
                      </Button>
                    </InputGroup>
                  </Form.Group>
                  <Select
                    name="communicationTreatment"
                    label="Communication Policy:">
                    <option value={CommunicationTreatment.STANDARD}>Standard</option>
                    <option value={CommunicationTreatment.ALLOWED_ONLY}>Allowed Contacts Only</option>
                  </Select>
                </Col>

                <Col md={4}>
                  <Select
                    name="status"
                    label="General Status:">
                    <option value={EnabledDisabled.ENABLED}>Enabled</option>
                    <option value={EnabledDisabled.DISABLED}>Disabled</option>
                  </Select>
                  <Select
                    name="phoneStatus"
                    label="Phone Status:">
                    <option value={EnabledDisabled.ENABLED}>Enabled</option>
                    <option value={EnabledDisabled.DISABLED}>Disabled</option>
                  </Select>
                  <Select
                    name="textStatus"
                    label="Texting Status:">
                    <option value={EnabledDisabled.ENABLED}>Enabled</option>
                    <option value={EnabledDisabled.DISABLED}>Disabled</option>
                  </Select>
                  <Select
                    name="audioStatus"
                    label="Wireless Audio Calling:">
                    <option value={EnabledDisabled.ENABLED}>Enabled</option>
                    <option value={EnabledDisabled.DISABLED}>Disabled</option>
                  </Select>
                  <Select
                    name="tvStreamingStatus"
                    label="TV Streaming Status:">
                    <option value={EnabledDisabled.ENABLED}>Enabled</option>
                    <option value={EnabledDisabled.DISABLED}>Disabled</option>
                  </Select>
                  <Select
                    name="voicemailStatus"
                    label="Voicemail Status:"
                    disabled>
                    <option value={EnabledDisabled.ENABLED}>Enabled</option>
                    <option value={EnabledDisabled.DISABLED}>Disabled</option>
                  </Select>
                  <Select
                    name="gamesEnabled"
                    label="Games Status:">
                    <option value={EnabledDisabled.ENABLED}>Enabled</option>
                    <option value={EnabledDisabled.DISABLED}>Disabled</option>
                  </Select>
                  <Select
                    name="referenceMaterialEnabled"
                    label="Reference Material Status:">
                    <option value={EnabledDisabled.ENABLED}>Enabled</option>
                    <option value={EnabledDisabled.DISABLED}>Disabled</option>
                  </Select>
                  <Select
                    name="faceLoginStatus"
                    label="Face Login:">
                    <option value={EnabledDisabled.ENABLED}>Enabled</option>
                    <option value={EnabledDisabled.DISABLED}>Disabled</option>
                  </Select>
                  <Select
                    name="recordFaceLoginVideo"
                    label="Face Login Recording:">
                    <option value="false">Disabled</option>
                    <option value="true">Enabled</option>
                  </Select>
                </Col>
              </Row>
            </Card.Body>

            <Card.Footer>
              <Button type="submit" variant="primary" disabled={submitting || hasValidationErrors}>
                {submitting ? 'Saving...' : 'Save'}
              </Button>
            </Card.Footer>
          </form>
        )} />
    </Card>
  );
};

export default InmateEdit;
